import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Flower2, Target, Compass, ListChecks, User } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1554554497-0095c34db3ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
  const [c, setC] = useState({});
  const { t, tr } = useLang();
  useEffect(() => { api.get("/public/content").then((r) => setC(r.data)).catch(() => {}); }, []);

  const about = c.about || {};
  const vm = c.vision_mission || {};
  const founder = c.founder || {};

  return (
    <div data-testid="public-about-page">
      <section className="bg-[#0E2916] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C59B27] font-mono">{t("About Us")}</p>
          <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-2">{t("About the Trust")}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-2 gap-12 items-center">
        <img src={ABOUT_IMG} alt="Trust" className="rounded-2xl shadow-xl w-full h-[400px] object-cover" />
        <div>
          <h2 className="font-display text-3xl font-semibold text-[#194324]">{tr(about, "heading") || "A spiritual and humanitarian trust"}</h2>
          <p className="mt-4 text-[#3A4E41] leading-relaxed whitespace-pre-line">
            {tr(about, "body") || "Based in Gandhidham–Kutch, Gujarat, Hatha Vidya Charitable Trust is devoted to the ancient science of Hatha Yoga alongside selfless community service. Through yoga, spirituality, Gau Seva, Annadan, education, health and environmental care, we serve seekers and society alike."}
          </p>
        </div>
      </section>

      <section className="bg-[#F2EEE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid md:grid-cols-3 gap-6">
          {[
            { icon: Compass, title: "Vision", body: tr(vm, "vision") || "To awaken health, harmony and spiritual well-being in every individual and serve all beings with compassion." },
            { icon: Target, title: "Mission", body: tr(vm, "mission") || "To spread yoga and spiritual knowledge while serving the poor, the needy, cows and the environment." },
            { icon: ListChecks, title: "Objectives", body: tr(vm, "objectives") || "Yoga education, Gau Seva, Annadan, welfare, health camps, education support and environmental care." },
          ].map((b) => (
            <div key={b.title} className="bg-white rounded-xl border border-[#E2DAD0] p-6">
              <b.icon className="h-7 w-7 text-[#C59B27] mb-3" />
              <h3 className="font-display text-xl font-semibold text-[#194324]">{t(b.title)}</h3>
              <p className="text-sm text-[#3A4E41] mt-2 leading-relaxed whitespace-pre-line">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
        <div className="inline-flex h-16 w-16 rounded-full bg-[#194324] items-center justify-center mb-4"><User className="h-8 w-8 text-[#C59B27]" /></div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">{t("Founder")}</p>
        <h2 className="font-display text-3xl font-semibold text-[#194324] mt-2">{founder.name || "Yogiraj Dawat Giri Ji"}</h2>
        <p className="text-[#C59B27] font-medium">{tr(founder, "title") || "Founder & Spiritual Guide"}</p>
        <p className="mt-5 text-[#3A4E41] leading-relaxed whitespace-pre-line">{tr(founder, "bio") || "Guided by decades of yogic practice and seva, our founder inspires the trust's mission of discipline, devotion and service to humanity and nature."}</p>
      </section>
    </div>
  );
}
